import { useCallback, useEffect, useRef, useState } from "react";

// ── Strip text for speech ─────────────────────────────────────────────────────

/** Strip Markdown formatting and emojis so text sounds natural when read aloud by TTS. */
export function stripForSpeech(text: string): string {
  return (
    text
      // Code blocks (``` … ```)
      .replace(/```[\s\S]*?```/g, "")
      // Inline code (`…`)
      .replace(/`[^`]*`/g, "")
      // Images ![alt](url)
      .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
      // Links [text](url) → text
      .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
      // Bold / italic markers (**, __, *, _)
      .replace(/(\*{1,3}|_{1,3})(.*?)\1/g, "$2")
      // Strikethrough ~~text~~
      .replace(/~~(.*?)~~/g, "$1")
      // Headings (# … )
      .replace(/^#{1,6}\s+/gm, "")
      // Horizontal rules (---, ***, ___)
      .replace(/^[-*_]{3,}\s*$/gm, "")
      // Unordered list markers (-, *, +)
      .replace(/^[\s]*[-*+]\s+/gm, "")
      // Ordered list markers (1., 2., …)
      .replace(/^[\s]*\d+\.\s+/gm, "")
      // Blockquotes (>)
      .replace(/^>\s?/gm, "")
      // HTML tags
      .replace(/<[^>]+>/g, "")
      // Emojis & miscellaneous symbols (Unicode ranges)
      .replace(
        /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{200D}\u{20E3}\u{E0020}-\u{E007F}]/gu,
        ""
      )
      // Collapse multiple whitespace / blank lines
      .replace(/\n{3,}/g, "\n\n")
      .replace(/[ \t]+/g, " ")
      .trim()
  );
}

// ── Speech-to-Text (Voice Transcription) ──────────────────────────────────────

type SpeechRecognitionEvent = Event & { results: SpeechRecognitionResultList };

/** Minimal interface for the Web Speech API SpeechRecognition object */
interface ISpeechRecognition extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: Event & { error?: string }) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

/** Browser-agnostic SpeechRecognition constructor */
function getSpeechRecognitionCtor(): (new () => ISpeechRecognition) | null {
  return (
    (window as any).SpeechRecognition ??
    (window as any).webkitSpeechRecognition ??
    null
  );
}

export interface SpeechToTextResult {
  /** Whether the browser supports the Web Speech Recognition API */
  supported: boolean;
  /** Whether the microphone is currently recording */
  isListening: boolean;
  /** Current (interim or final) transcript text */
  transcript: string;
  /** Start voice recognition. `onResult` is called with the final transcript. */
  startListening(onResult?: (text: string) => void): void;
  /** Stop voice recognition */
  stopListening(): void;
  /** Toggle listening on/off */
  toggleListening(onResult?: (text: string) => void): void;
}

/**
 * React hook for speech-to-text using the Web Speech Recognition API.
 * Returns `supported: false` on browsers that do not implement the API.
 */
export function useSpeechToText(lang: string = "de-DE"): SpeechToTextResult {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [supported, setSupported] = useState(false);
  const recognitionRef = useRef<ISpeechRecognition | null>(null);

  useEffect(() => {
    setSupported(getSpeechRecognitionCtor() != null);
  }, []);

  const startListening = useCallback(
    (onResult?: (text: string) => void) => {
      const Ctor = getSpeechRecognitionCtor();
      if (!Ctor) return;
      // Stop any running instance
      recognitionRef.current?.abort();

      const recognition = new Ctor();
      recognition.lang = lang;
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        const results = event.results;
        let finalTranscript = "";
        let interimTranscript = "";
        for (let i = 0; i < results.length; i++) {
          const result = results[i];
          if (result.isFinal) {
            finalTranscript += result[0].transcript;
          } else {
            interimTranscript += result[0].transcript;
          }
        }
        const text = finalTranscript || interimTranscript;
        setTranscript(text);
        if (finalTranscript) {
          onResult?.(finalTranscript);
        }
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.onerror = (event: Event & { error?: string }) => {
        console.warn("SpeechRecognition error:", event.error);
        setIsListening(false);
      };

      recognitionRef.current = recognition;
      recognition.start();
      setIsListening(true);
      setTranscript("");
    },
    [lang]
  );

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  const toggleListening = useCallback(
    (onResult?: (text: string) => void) => {
      if (isListening) {
        stopListening();
      } else {
        startListening(onResult);
      }
    },
    [isListening, startListening, stopListening]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
    };
  }, []);

  return { isListening, transcript, supported, startListening, stopListening, toggleListening };
}

// ── Text-to-Speech ────────────────────────────────────────────────────────────

export interface TextToSpeechResult {
  /** Whether the browser supports the Web Speech Synthesis API */
  supported: boolean;
  /** Whether speech is currently playing */
  isSpeaking: boolean;
  /** Speak the given text. Automatically strips Markdown/emojis via `stripForSpeech`. */
  speak(text: string): void;
  /** Stop any running speech */
  stopSpeaking(): void;
}

/**
 * React hook for text-to-speech using the Web Speech Synthesis API.
 * Automatically strips Markdown formatting and emojis before speaking.
 * Returns `supported: false` on browsers that do not implement the API.
 */
export function useTextToSpeech(lang: string = "de-DE"): TextToSpeechResult {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [supported, setSupported] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  useEffect(() => {
    setSupported(typeof window !== "undefined" && "speechSynthesis" in window);
  }, []);

  const speak = useCallback(
    (text: string) => {
      if (!("speechSynthesis" in window)) return;
      // Cancel any running speech
      window.speechSynthesis.cancel();

      const cleaned = stripForSpeech(text);
      if (!cleaned) return;

      const utterance = new SpeechSynthesisUtterance(cleaned);
      utterance.lang = lang;
      utterance.rate = 1.0;
      utterance.pitch = 1.0;

      // Try to find a matching voice
      const voices = window.speechSynthesis.getVoices();
      const langPrefix = lang.split("-")[0];
      const voice =
        voices.find((v) => v.lang === lang) ??
        voices.find((v) => v.lang.startsWith(langPrefix));
      if (voice) utterance.voice = voice;

      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);

      utteranceRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    },
    [lang]
  );

  const stopSpeaking = useCallback(() => {
    window.speechSynthesis?.cancel();
    setIsSpeaking(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      window.speechSynthesis?.cancel();
    };
  }, []);

  return { isSpeaking, supported, speak, stopSpeaking };
}

